import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Receipt, Search } from 'lucide-react';
import { EmptyState } from '../../shared/components/EmptyState';
import { Modal } from '../../shared/components/Modal';
import { Skeleton } from '../../shared/components/Skeleton';
import { formatBRL, formatDateTime } from '../../shared/lib/format';
import { relatoriosApi, type VendaDetalhadaRow } from '../relatorios/api';

const FORMA_LABEL: Record<string, string> = {
  dinheiro: 'Dinheiro',
  pix: 'PIX',
  cartao_debito: 'Debito',
  cartao_credito: 'Credito',
  vale_refeicao: 'VR',
  outro: 'Outro',
};

function startOfTodayISO() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

export function VendasPage() {
  const [periodo, setPeriodo] = useState<'hoje' | '7d' | '30d'>('hoje');
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<VendaDetalhadaRow | null>(null);

  const inicioIso = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    if (periodo === '7d') d.setDate(d.getDate() - 6);
    else if (periodo === '30d') d.setDate(d.getDate() - 29);
    return periodo === 'hoje' ? startOfTodayISO() : d.toISOString();
  }, [periodo]);

  const { data, isLoading } = useQuery({
    queryKey: ['vendas-detalhadas', periodo],
    queryFn: () => relatoriosApi.vendasDetalhadas({ inicio: inicioIso }),
    refetchInterval: 20_000,
  });

  const rows = (data?.rows ?? []).filter((r) => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return (
      String(r.numero).includes(s) ||
      (r.operador?.nome ?? '').toLowerCase().includes(s) ||
      r.itens.some((i) => i.produto_nome.toLowerCase().includes(s))
    );
  });

  const total = rows.filter((r) => r.status === 'paga').reduce((a, r) => a + Number(r.total), 0);

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Vendas</h1>
          <p className="mt-1 text-sm text-slate-500">
            Historico de vendas com busca e detalhamento completo.
          </p>
        </div>
        <div className="text-right">
          <div className="text-xs text-slate-500">Total no periodo</div>
          <div className="text-xl font-bold text-brand-700">{formatBRL(total)}</div>
        </div>
      </header>

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex gap-1">
          {(['hoje', '7d', '30d'] as const).map((p) => (
            <button
              key={p}
              onClick={() => setPeriodo(p)}
              className={
                periodo === p
                  ? 'rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white'
                  : 'rounded-md px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100'
              }
            >
              {p === 'hoje' ? 'Hoje' : p === '7d' ? '7 dias' : '30 dias'}
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[220px]">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            className="input pl-9"
            placeholder="Buscar por numero, produto ou operador"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-16" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={Receipt}
          title="Nenhuma venda no periodo"
          description="Amplie o intervalo ou registre novas vendas no PDV."
        />
      ) : (
        <ul className="divide-y divide-slate-200 overflow-hidden rounded-xl border border-slate-200 bg-white">
          {rows.map((v) => (
            <li key={v.id}>
              <button
                type="button"
                onClick={() => setSelected(v)}
                className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-slate-50"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-semibold text-slate-900">#{v.numero}</span>
                    <span
                      className={
                        v.status === 'paga'
                          ? 'rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] text-emerald-700'
                          : 'rounded-full bg-red-50 px-2 py-0.5 text-[11px] text-red-700'
                      }
                    >
                      {v.status}
                    </span>
                    <span className="text-xs text-slate-500">
                      {formatDateTime(v.criado_em)}
                    </span>
                  </div>
                  <div className="truncate text-xs text-slate-500">
                    {v.operador?.nome ?? 'Operador'} ·{' '}
                    {v.itens.length} {v.itens.length === 1 ? 'item' : 'itens'}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-semibold text-slate-900">
                    {formatBRL(Number(v.total))}
                  </div>
                  <div className="text-xs text-slate-500">
                    {v.pagamentos.map((p) => FORMA_LABEL[p.forma] ?? p.forma).join(', ')}
                  </div>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}

      <Modal
        open={!!selected}
        onClose={() => setSelected(null)}
        title={selected ? `Venda #${selected.numero}` : ''}
        size="md"
      >
        {selected && (
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-2 rounded-lg bg-slate-50 p-3">
              <div>
                <div className="text-xs text-slate-500">Data</div>
                <div className="font-medium">{formatDateTime(selected.criado_em)}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500">Operador</div>
                <div className="font-medium">{selected.operador?.nome ?? '-'}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500">Status</div>
                <div className="font-medium capitalize">{selected.status}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500">Total</div>
                <div className="font-bold text-brand-700">
                  {formatBRL(Number(selected.total))}
                </div>
              </div>
            </div>

            <div>
              <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Itens
              </h3>
              <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
                {selected.itens.map((it, i) => (
                  <li key={i} className="flex justify-between p-2 text-sm">
                    <span>
                      {Number(it.quantidade).toLocaleString('pt-BR')}x {it.produto_nome}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Pagamentos
              </h3>
              <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
                {selected.pagamentos.map((p, i) => (
                  <li key={i} className="flex justify-between p-2 text-sm">
                    <span>{FORMA_LABEL[p.forma] ?? p.forma}</span>
                    <span className="font-medium">{formatBRL(Number(p.valor))}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
